import { Link, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { CollectionImage } from "@/components/collections/CollectionImage";
import { ProductCard } from "@/components/shop/ProductCard";
import { getCollection, getCollectionPatterns, getCollectionProducts, textureSrc } from "@/lib/catalog";
import { usePageMeta } from "@/lib/seo";
import NotFoundPage from "./NotFoundPage";

function CollectionBody({ collection: c }) {
  const products = getCollectionProducts(c);
  const patterns = getCollectionPatterns(c);
  usePageMeta(c.name, c.description || `${c.label}: peças feitas à mão pela Alexandra.`);
  const hasImage = !!c.cover?.id;
  return (
    <div data-testid="collection-page">
      <section className={`relative overflow-hidden ${hasImage ? "bg-[#3d332d] text-ivory" : "bg-rose-soft text-ink"}`}>
        {hasImage && (
          <>
            <CollectionImage image={c.cover} alt="" eager className="absolute inset-0 h-full w-full object-cover" />
            <span className="absolute inset-0 bg-[rgba(43,36,32,.46)]" aria-hidden="true" />
          </>
        )}
        <div className="bb-container relative z-10 flex min-h-[320px] flex-col justify-end pb-10 pt-24 sm:min-h-[400px] sm:pb-14 lg:min-h-[460px]">
          <Link to="/loja" className={`absolute left-5 top-5 inline-flex min-h-[44px] items-center gap-1 text-sm sm:left-8 lg:left-10 ${hasImage ? "text-ivory hover:text-rose" : "text-taupe hover:text-bordeaux"}`}><ChevronLeft className="h-4 w-4" /> Loja</Link>
          {c.status === "draft" && <span className="bb-placeholder-tag !left-auto right-3">Rascunho (pré-visualização)</span>}
          <div className="grid max-w-[640px] gap-4">
            <p className={`bb-eyebrow ${hasImage ? "!text-ivory" : ""}`}>{c.label}</p>
            <h1 data-testid="collection-title" className={`bb-h1 ${hasImage ? "text-ivory [text-shadow:0_1px_18px_rgba(30,24,20,.3)]" : "text-ink"}`}>{c.name}</h1>
            {c.description && <p data-testid="collection-description" className={`max-w-[48ch] text-[17px] leading-[1.55] lg:text-[19px] ${hasImage ? "text-ivory [text-shadow:0_1px_12px_rgba(30,24,20,.3)]" : "text-taupe"}`}>{c.description}</p>}
          </div>
        </div>
      </section>

      {patterns.length > 0 && (
        <section className="bb-container pt-14 sm:pt-16" data-testid="collection-patterns" aria-label="Tecidos da coleção">
          <p className="bb-eyebrow mb-4">Tecidos da coleção</p>
          <ul className="flex gap-4 overflow-x-auto pb-2 [scrollbar-width:none]">
            {patterns.map((p) => (
              <li key={p.id} className="w-[88px] shrink-0 text-center">
                <Link to={`/loja?colecao=${c.slug}&tecido=${p.id}`} className="group block">
                  <span className="block aspect-square overflow-hidden rounded-md shadow-[inset_0_0_0_1px_rgba(64,55,49,.1)]">
                    <img src={textureSrc(p.texture, 240)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </span>
                  <span className="mt-2 block text-[12px] leading-tight text-taupe-strong group-hover:text-ink">{p.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="bb-container py-14 sm:py-16 lg:py-20" data-testid="collection-products">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <h2 className="bb-h2 text-ink">As peças da coleção</h2>
          <Link to={`/loja?colecao=${c.slug}`} data-testid="collection-open-shop" className="bb-link text-sm font-medium">Ver na loja, com filtros</Link>
        </div>
        {products.length === 0 ? (
          <p className="rounded-lg bg-linen p-8 text-center text-sm text-taupe">Ainda não há peças nesta coleção.</p>
        ) : (
          <div className="grid grid-cols-2 gap-x-3 gap-y-7 sm:gap-x-5 sm:gap-y-10 lg:grid-cols-4 lg:gap-x-6 lg:gap-y-12">
            {products.map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}

export default function CollectionPage() {
  const { slug } = useParams();
  const collection = getCollection(slug);
  if (!collection) return <NotFoundPage />;
  return <CollectionBody key={collection.slug} collection={collection} />;
}
